import { makeAutoObservable, runInAction } from 'mobx';
import { apiGetUserConfig, apiSaveUserConfig } from '../../api/roster';
import { LocalEvent } from '../../constants/event';
import {
  buildTabRouteCacheKey,
  isOrgRootPathname,
  resolveRouteTabMeta,
} from '../../router/routeMeta';
import { allowsTabStoreStateCache } from '../../utils/tabStateCachePolicy';

/** 历史误建的组织根「页面未找到」幽灵标签 */
const isGhostOrgRootTab = (tab: TabItem) =>
  tab.key === '/' || isOrgRootPathname(tab.path);

export type TabItem = {
  key: string;
  title: string;
  path: string;
  search: string;
  fullPath: string;
  closable: boolean;
  tabMode: 'none' | 'reuse' | 'new';
  cacheStrategy: 'none' | 'state' | 'instance';
  dirty: boolean;
  keepAlive: boolean;
  dynamicTitle?: boolean;
  openedAt: number;
  lastActivatedAt: number;
};

const MAX_TAB_COUNT = 10;
const TAB_STORAGE_KEY_PREFIX = 'ehr.tab-store.v2';
const TAB_STORAGE_USER_MARK_KEY = `${TAB_STORAGE_KEY_PREFIX}:active-user`;
const LEGACY_TAB_STORAGE_KEY = 'ehr.tab-store.v1';
const TAB_ITEMS_CONFIG_CODE = 'tab_items_config';
const TAB_ITEMS_CLOUD_SAVE_DELAY = 300;

type PersistedTabStore = {
  tabs: TabItem[];
  activeKey: string;
};

export default class TabStore {
  tabs: TabItem[] = [];
  activeKey = '';
  tabKeyIndex: Record<string, string> = {};
  lruQueue: string[] = [];
  pausedTabKeys: Record<string, boolean> = {};
  stateCache: Record<string, unknown> = {};
  restoreVersion = 0;
  private storageUserId = '';
  private cloudSaveTimer = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  private canUseStorage() {
    return typeof window !== 'undefined' && Boolean(window.localStorage);
  }

  private getStorageKeyByUserId(userId: string) {
    return `${TAB_STORAGE_KEY_PREFIX}:user:${userId}`;
  }

  private getPersistedPayload(): PersistedTabStore {
    return {
      tabs: this.tabs,
      activeKey: this.activeKey,
    };
  }

  private savePayloadToStorage(userId: string, payload: PersistedTabStore) {
    window.localStorage.setItem(
      this.getStorageKeyByUserId(userId),
      JSON.stringify(payload)
    );
  }

  private persistToStorage() {
    if (!this.storageUserId) return;
    if (!this.canUseStorage()) return;
    try {
      const payload = this.getPersistedPayload();
      this.savePayloadToStorage(this.storageUserId, payload);
      this.schedulePersistToCloud(payload);
    } catch (error) {
      console.error('persist tab store failed:', error);
    }
  }

  private schedulePersistToCloud(payload: PersistedTabStore) {
    if (this.cloudSaveTimer) {
      window.clearTimeout(this.cloudSaveTimer);
    }
    this.cloudSaveTimer = window.setTimeout(() => {
      this.persistToCloud(payload);
    }, TAB_ITEMS_CLOUD_SAVE_DELAY);
  }

  private async persistToCloud(payload: PersistedTabStore) {
    try {
      await apiSaveUserConfig({
        code: TAB_ITEMS_CONFIG_CODE,
        config: JSON.stringify(payload),
      });
    } catch (error) {
      console.warn('persist tab items config failed:', error);
    }
  }

  private parsePersistedPayload(raw: unknown): Partial<PersistedTabStore> {
    const value =
      raw && typeof raw === 'object' && 'config' in raw
        ? (raw as { config?: unknown }).config
        : raw;

    if (typeof value === 'string') {
      return JSON.parse(value) as Partial<PersistedTabStore>;
    }

    return (value || {}) as Partial<PersistedTabStore>;
  }

  private applyPersistedPayload(parsed: Partial<PersistedTabStore>) {
    const safeTabs = Array.isArray(parsed.tabs) ? parsed.tabs : [];
    const dedupedTabs: TabItem[] = [];
    const visited = new Set<string>();
    safeTabs.forEach((tab) => {
      if (!tab || typeof tab.key !== 'string' || visited.has(tab.key)) return;
      if (isGhostOrgRootTab(tab)) return;
      visited.add(tab.key);
      // 不再有固定入口；仅由 TabBar「最后一个标签」限制关闭
      dedupedTabs.push({ ...tab, closable: true });
    });
    this.tabs = dedupedTabs.slice(0, MAX_TAB_COUNT);
    const restoredActiveKey =
      typeof parsed.activeKey === 'string' ? parsed.activeKey : '';
    this.activeKey = this.tabs.some((tab) => tab.key === restoredActiveKey)
      ? restoredActiveKey
      : this.tabs[this.tabs.length - 1]?.key ?? '';
    this.lruQueue = this.tabs.map((tab) => tab.key);
    this.syncIndexes();
    this.tabs.forEach((item) =>
      this.markTabActivity(item.key, item.key === this.activeKey)
    );
    this.bumpRestoreVersion();
  }

  private bumpRestoreVersion() {
    this.restoreVersion += 1;
  }

  /** @returns 是否已成功应用本地持久化数据 */
  private restoreFromStorage(userId: string): boolean {
    if (!this.canUseStorage()) return false;
    try {
      const raw = window.localStorage.getItem(
        this.getStorageKeyByUserId(userId)
      );
      if (!raw) return false;
      this.applyPersistedPayload(this.parsePersistedPayload(raw));
      return true;
    } catch (error) {
      console.error('restore tab store failed:', error);
      return false;
    }
  }

  private async restoreFromCloud(userId: string) {
    try {
      const response = (await apiGetUserConfig({
        code: TAB_ITEMS_CONFIG_CODE,
      })) as unknown;
      const parsed = this.parsePersistedPayload(response);
      if (!Array.isArray(parsed.tabs)) return;

      runInAction(() => {
        if (this.storageUserId !== userId) return;
        this.applyPersistedPayload(parsed);
        if (this.canUseStorage()) {
          this.savePayloadToStorage(userId, this.getPersistedPayload());
        }
      });
    } catch (error) {
      console.warn('restore tab items config failed:', error);
    }
  }

  bindStorageUser(userId?: string | number) {
    if (!this.canUseStorage()) return;
    const nextUserId = `${userId ?? ''}`.trim();
    if (!nextUserId || nextUserId === this.storageUserId) return;
    const previousUserId =
      window.localStorage.getItem(TAB_STORAGE_USER_MARK_KEY) || '';
    if (previousUserId && previousUserId !== nextUserId) {
      window.localStorage.removeItem(
        this.getStorageKeyByUserId(previousUserId)
      );
    }
    window.localStorage.setItem(TAB_STORAGE_USER_MARK_KEY, nextUserId);
    window.localStorage.removeItem(LEGACY_TAB_STORAGE_KEY);
    this.storageUserId = nextUserId;
    this.tabs = [];
    this.activeKey = '';
    this.tabKeyIndex = {};
    this.lruQueue = [];
    this.pausedTabKeys = {};
    const restoredFromStorage = this.restoreFromStorage(nextUserId);
    if (!restoredFromStorage) {
      this.bumpRestoreVersion();
    } else {
      // 回写剔除幽灵标签后的结果，避免下次再灌回
      this.persistToStorage();
    }
    this.restoreFromCloud(nextUserId);
  }

  private syncIndexes() {
    this.tabKeyIndex = {};
    this.tabs.forEach((tab) => {
      this.tabKeyIndex[tab.key] = tab.key;
    });
  }

  private touchLru(key: string) {
    this.lruQueue = this.lruQueue.filter((item) => item !== key);
    this.lruQueue.push(key);
  }

  private findByKey(key: string) {
    return this.tabs.find((tab) => tab.key === key);
  }

  private removeByKey(key: string) {
    const target = this.findByKey(key);
    if (!target) return undefined;
    this.tabs = this.tabs.filter((tab) => tab.key !== key);
    this.lruQueue = this.lruQueue.filter((item) => item !== key);
    delete this.pausedTabKeys[key];
    this.clearStateCacheKeysForTab(target);
    this.syncIndexes();
    return target;
  }

  private clearStateCacheKeysForTabs(tabs: TabItem[]) {
    tabs.forEach((tab) => this.clearStateCacheKeysForTab(tab));
  }

  private pickClosableLru() {
    for (const key of this.lruQueue) {
      const tab = this.findByKey(key);
      const blockedByDirtyInstance =
        tab?.cacheStrategy === 'instance' && tab?.dirty;
      if (
        tab &&
        tab.closable &&
        key !== this.activeKey &&
        !blockedByDirtyInstance
      ) {
        return tab;
      }
    }
    return undefined;
  }

  openTab(input: {
    key: string;
    title: string;
    path: string;
    search?: string;
    fullPath: string;
    closable?: boolean;
    tabMode?: 'none' | 'reuse' | 'new';
    cacheStrategy?: 'none' | 'state' | 'instance';
    keepAlive?: boolean;
    forceTitle?: boolean;
    resetDynamicTitle?: boolean;
  }) {
    if (input.key === '/' || isOrgRootPathname(input.path)) {
      return {
        type: 'activate-existing' as const,
        activeKey: this.activeKey,
      };
    }
    const now = Date.now();
    const syncTabMeta = (tab: TabItem) => {
      tab.path = input.path;
      tab.search = input.search || '';
      tab.fullPath = input.fullPath;
      if (input.resetDynamicTitle) {
        tab.dynamicTitle = false;
      } else if (input.title && (input.forceTitle || !tab.dynamicTitle)) {
        tab.title = input.title;
        if (input.forceTitle) {
          tab.dynamicTitle = true;
        }
      }
      tab.closable = input.closable !== false;
      tab.tabMode = input.tabMode || tab.tabMode;
      tab.cacheStrategy = input.cacheStrategy || tab.cacheStrategy;
      if (typeof input.keepAlive === 'boolean') {
        tab.keepAlive = input.keepAlive;
      }
    };
    const fixedEntry = this.tabs.find(
      (tab) => !tab.closable && tab.path === input.path
    );
    if (fixedEntry) {
      syncTabMeta(fixedEntry);
      this.activateTab(fixedEntry.key);
      return {
        type: 'activate-existing' as const,
        activeKey: fixedEntry.key,
      };
    }

    const existedKey = this.tabKeyIndex[input.key];
    if (existedKey) {
      const existedTab = this.findByKey(existedKey);
      if (existedTab) {
        syncTabMeta(existedTab);
      }
      this.activateTab(existedKey);
      return {
        type: 'activate-existing' as const,
        activeKey: existedKey,
      };
    }

    if (this.tabs.length >= MAX_TAB_COUNT) {
      const evictTab = this.pickClosableLru();
      if (!evictTab) {
        const hasClosableCandidate = this.tabs.some(
          (tab) => tab.closable && tab.key !== this.activeKey
        );
        return {
          type: 'rejected-by-limit' as const,
          reason: hasClosableCandidate
            ? ('dirty-instance-blocked' as const)
            : ('no-closable-tab' as const),
          activeKey: this.activeKey,
        };
      }
      this.removeByKey(evictTab.key);
    }

    const tab: TabItem = {
      key: input.key,
      title: input.title,
      path: input.path,
      search: input.search || '',
      fullPath: input.fullPath,
      closable: input.closable !== false,
      tabMode: input.tabMode || 'reuse',
      cacheStrategy: input.cacheStrategy || 'none',
      dirty: false,
      keepAlive: Boolean(input.keepAlive),
      dynamicTitle: Boolean(input.forceTitle),
      openedAt: now,
      lastActivatedAt: now,
    };

    this.tabs.push(tab);
    this.activeKey = tab.key;
    this.touchLru(tab.key);
    this.syncIndexes();
    this.markTabActivity(tab.key, true);
    this.persistToStorage();

    return {
      type: 'opened' as const,
      activeKey: tab.key,
    };
  }

  activateTab(key: string) {
    const tab = this.findByKey(key);
    if (!tab) return;
    const previousActiveKey = this.activeKey;
    tab.lastActivatedAt = Date.now();
    this.activeKey = key;
    this.touchLru(key);
    this.tabs.forEach((item) =>
      this.markTabActivity(item.key, item.key === key)
    );
    this.persistToStorage();
    if (
      typeof window !== 'undefined' &&
      previousActiveKey &&
      previousActiveKey !== key
    ) {
      window.dispatchEvent(
        new CustomEvent(LocalEvent.TAB_ACTIVATED, {
          detail: {
            tabKey: key,
            fullPath: tab.fullPath,
            path: tab.path,
            search: tab.search,
          },
        })
      );
    }
  }

  closeTab(key: string) {
    if (this.tabs.length <= 1) return undefined;

    const currentIndex = this.tabs.findIndex((tab) => tab.key === key);
    if (currentIndex === -1) return undefined;

    const target = this.tabs[currentIndex];
    if (!target?.closable) return undefined;

    const fallbackTab =
      this.tabs[currentIndex + 1] || this.tabs[currentIndex - 1] || undefined;

    this.removeByKey(key);

    if (this.activeKey === key) {
      this.activeKey = fallbackTab?.key || '';
      if (this.activeKey) {
        this.touchLru(this.activeKey);
      }
    }

    this.tabs.forEach((item) =>
      this.markTabActivity(item.key, item.key === this.activeKey)
    );
    this.persistToStorage();

    return fallbackTab;
  }

  closeOthers(key: string) {
    const keepTab = this.findByKey(key);
    if (!keepTab) return;
    const closedTabs = this.tabs.filter(
      (tab) => tab.closable && tab.key !== key
    );
    this.tabs = this.tabs.filter((tab) => !tab.closable || tab.key === key);
    this.clearStateCacheKeysForTabs(closedTabs);
    this.activeKey = key;
    this.lruQueue = this.tabs.map((tab) => tab.key);
    this.syncIndexes();
    this.tabs.forEach((item) =>
      this.markTabActivity(item.key, item.key === key)
    );
    this.persistToStorage();
  }

  closeRight(key: string) {
    const currentIndex = this.tabs.findIndex((tab) => tab.key === key);
    if (currentIndex === -1) return;
    const left = this.tabs.slice(0, currentIndex + 1);
    const rightTabs = this.tabs.slice(currentIndex + 1);
    const closedTabs = rightTabs.filter((tab) => tab.closable);
    const right = rightTabs.filter((tab) => !tab.closable);
    this.tabs = [...left, ...right];
    this.clearStateCacheKeysForTabs(closedTabs);
    if (!this.findByKey(this.activeKey) && this.tabs.length) {
      this.activeKey = key;
    }
    this.lruQueue = this.lruQueue.filter((item) =>
      this.tabs.some((tab) => tab.key === item)
    );
    this.syncIndexes();
    this.tabs.forEach((item) =>
      this.markTabActivity(item.key, item.key === this.activeKey)
    );
    this.persistToStorage();
  }

  moveTab(activeKey: string, overKey: string) {
    if (!activeKey || !overKey || activeKey === overKey) return;
    const fromIndex = this.tabs.findIndex((tab) => tab.key === activeKey);
    const toIndex = this.tabs.findIndex((tab) => tab.key === overKey);
    if (fromIndex === -1 || toIndex === -1) return;
    const nextTabs = [...this.tabs];
    const [moved] = nextTabs.splice(fromIndex, 1);
    nextTabs.splice(toIndex, 0, moved);
    this.tabs = nextTabs;
    this.syncIndexes();
    this.persistToStorage();
  }

  markTabActivity(key: string, isActive: boolean) {
    this.pausedTabKeys[key] = !isActive;
  }

  shouldPauseEffects(key: string) {
    return Boolean(this.pausedTabKeys[key]);
  }

  updateTabTitle(key: string, title: string, options?: { dynamic?: boolean }) {
    const tab = this.findByKey(key);
    if (!tab || !title) return;
    if (tab.dynamicTitle && !options?.dynamic) return;
    const changed = tab.title !== title;
    if (changed) {
      tab.title = title;
    }
    if (options?.dynamic) {
      tab.dynamicTitle = true;
    }
    if (changed || options?.dynamic) {
      this.persistToStorage();
    }
  }

  markTabDirty(key: string, dirty: boolean) {
    const tab = this.findByKey(key);
    if (!tab) return;
    tab.dirty = dirty;
  }

  clearAll(userId?: string | number) {
    this.tabs = [];
    this.activeKey = '';
    this.tabKeyIndex = {};
    this.lruQueue = [];
    this.pausedTabKeys = {};
    this.stateCache = {};

    const targetUserId = `${userId ?? this.storageUserId}`.trim();
    if (!targetUserId || !this.canUseStorage()) return;
    this.storageUserId = targetUserId;
    window.localStorage.removeItem(this.getStorageKeyByUserId(targetUserId));
    this.schedulePersistToCloud(this.getPersistedPayload());
  }

  private clearStateCacheKeysForTab(tab: TabItem) {
    const keys = new Set<string>();
    keys.add(tab.key);
    keys.add(tab.fullPath);
    keys.add(buildTabRouteCacheKey(tab.path, tab.search));
    const routeMeta = resolveRouteTabMeta(tab.path, tab.search, tab.fullPath);
    Object.keys(this.stateCache).forEach((cacheKey) => {
      if (keys.has(cacheKey)) {
        delete this.stateCache[cacheKey];
        return;
      }
      try {
        const origin =
          typeof window === 'undefined'
            ? 'http://localhost'
            : window.location.origin;
        const cacheUrl = new URL(cacheKey, origin);
        const cacheMeta = resolveRouteTabMeta(
          cacheUrl.pathname,
          cacheUrl.search,
          cacheKey
        );
        if (cacheMeta.tabKey === routeMeta.tabKey) {
          delete this.stateCache[cacheKey];
        }
      } catch {
        /* 非路由缓存 key 无需按 tabKey 兜底清理 */
      }
    });
  }

  getKeepAliveIncludeKeys() {
    const keys = new Set<string>();
    this.tabs.forEach((tab) => {
      if (tab.cacheStrategy !== 'instance') return;
      const meta = resolveRouteTabMeta(tab.path, tab.search, tab.fullPath);
      keys.add(meta.keepAliveCacheKey || meta.tabKey || tab.key);
    });
    return Array.from(keys);
  }

  setTabStateCache(key: string, state: unknown) {
    if (!allowsTabStoreStateCache(key)) {
      return;
    }
    this.stateCache[key] = state;
  }

  getTabStateCache<T>(key: string) {
    if (!allowsTabStoreStateCache(key)) {
      return undefined;
    }
    return this.stateCache[key] as T | undefined;
  }

  clearTabStateCache(key: string) {
    delete this.stateCache[key];
  }
}
